import { $, $$, pageData, setLoading } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { findSelectMenu, upgradeSelects } from '../lib/select-menu.js';
import { toast } from '../lib/toast.js';

/**
 * The two pages of the Polls module.
 *
 * The list carries three immediate actions — close, cancel, delete — which wait
 * for no save bar: they are not settings, they are gestures that publish a
 * result or destroy one.
 */

interface PollOption {
  key: string;
  label: string;
  emoji: string;
  voterIds: string[];
}

interface Poll {
  _id: string;
  question: string;
  description: string;
  channelId: string;
  endsAt: string | null;
  options: PollOption[];
  multiple: boolean;
  maxChoices: number;
  liveResults: boolean;
  anonymous: boolean;
  allowedRoleIds: string[];
  blockedRoleIds: string[];
  allowRetract: boolean;
}

interface EditorData {
  poll: Poll;
  guildId: string;
}

/* ------------------------------------------------------------------- liste */

export function initPollsListPage(): void {
  const list = $('#poll-list');
  if (!list) return;

  const { guildId } = pageData<{ guildId: string }>();

  list.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const actions = [
      { attribute: 'data-close', path: 'close', confirm: 'confirmClosePoll', done: 'pollClosed' },
      {
        attribute: 'data-cancel',
        path: 'cancel',
        confirm: 'confirmCancelPoll',
        done: 'pollCancelled',
      },
    ] as const;

    for (const action of actions) {
      const button = target.closest<HTMLButtonElement>(`[${action.attribute}]`);
      if (!button) continue;

      const pollId = button.getAttribute(action.attribute) ?? '';
      if (!pollId || !confirm(ct(action.confirm, 'Are you sure?'))) return;

      void run(button, async () => {
        await api.post(`/api/polls/${action.path}?guildId=${guildId}`, { guildId, pollId });
        toast(ct(action.done, 'Done.'), 'success');
        location.reload();
      });
      return;
    }

    const remove = target.closest<HTMLButtonElement>('[data-remove]');
    if (!remove) return;

    const pollId = remove.getAttribute('data-remove') ?? '';
    if (!pollId || !confirm(ct('confirmDeletePoll', 'Delete this poll?'))) return;

    void run(remove, async () => {
      await api.delete(`/api/polls/${pollId}?guildId=${guildId}`);
      toast(ct('pollDeleted', 'Poll deleted.'), 'success');
      location.reload();
    });
  });
}

/** Runs a list action: a loading indicator, errors as a toast. */
async function run(button: HTMLButtonElement, action: () => Promise<void>): Promise<void> {
  setLoading(button, true);
  try {
    await action();
  } catch (error) {
    toast(error instanceof Error ? error.message : String(error), 'error');
  } finally {
    setLoading(button, false);
  }
}

/* ------------------------------------------------------------------ édition */

export function initPollsEditorPage(): void {
  const form = $<HTMLFormElement>('[data-poll-form]');
  if (!form) return;

  const { poll, guildId } = pageData<EditorData>();

  wireOptionRows(form);
  fillForm(form, poll as unknown as Record<string, unknown>);

  // `datetime-local` accepts neither full ISO nor the `Z`: it wants the local
  // minute, with no zone. A poll with no deadline leaves the field empty.
  setValue(form, 'endsAt', poll.endsAt ? toLocalInput(poll.endsAt) : '');

  rebuildOptions(form, poll.options);

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = poll.channelId ? [poll.channelId] : [];

  for (const field of ['allowedRoleIds', 'blockedRoleIds'] as const) {
    const menu = findSelectMenu(form, field);
    if (menu) menu.value = poll[field];
  }

  for (const flag of ['multiple', 'liveResults', 'anonymous', 'allowRetract'] as const) {
    const node = form.elements.namedItem(flag);
    if (node instanceof HTMLInputElement) node.checked = poll[flag];
  }

  mountResourceEditor({
    endpoint: 'polls',
    listUrl: `/modules/polls?guildId=${guildId}`,
    guildId,
    form,
    itemId: poll._id,

    serialize: () => {
      const deadline = text(form, 'endsAt');

      return {
        question: text(form, 'question'),
        description: text(form, 'description'),
        channelId: findSelectMenu(form, 'channelId')?.single ?? '',

        /*
         * Sent back as ISO, and therefore in UTC — or `null`.
         *
         * The field is entered in the browser's zone: "20:00" means 20:00 to
         * whoever types it. Converting here is what makes the server and the
         * bot talk about the same instant. Empty means "no deadline", not
         * "now".
         */
        endsAt: deadline ? new Date(deadline).toISOString() : null,

        options: $$<HTMLElement>('[data-option-row]', form)
          .map((row) => ({
            label: fieldValue(row, 'label'),
            emoji: fieldValue(row, 'emoji'),
          }))
          .filter((option) => option.label),

        multiple: checked(form, 'multiple'),
        maxChoices: number(form, 'maxChoices') || 1,
        liveResults: checked(form, 'liveResults'),
        anonymous: checked(form, 'anonymous'),

        allowedRoleIds: findSelectMenu(form, 'allowedRoleIds')?.value ?? [],
        blockedRoleIds: findSelectMenu(form, 'blockedRoleIds')?.value ?? [],
        allowRetract: checked(form, 'allowRetract'),
      };
    },

    labels: {
      saved: ct('pollSaved', 'Poll saved.'),
      confirmDelete: ct('confirmDeletePoll', 'Delete this poll?'),
      deleted: ct('pollDeleted', 'Poll deleted.'),
    },
  });
}

/* ------------------------------------------------------------ lignes d'option */

let added = 0;

function wireOptionRows(form: HTMLFormElement): void {
  form.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    if (target.closest('[data-add="option"]')) {
      addOption(form);
      return;
    }

    const remove = target.closest('[data-remove]');
    if (remove) {
      remove.closest('[data-option-row]')?.remove();
      form.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

function addOption(form: HTMLFormElement): HTMLElement | null {
  const blueprint = $<HTMLTemplateElement>('[data-template="option"]');
  const list = $<HTMLElement>('[data-list="option"]', form);
  if (!blueprint || !list) return null;

  added += 1;

  const holder = document.createElement('div');
  holder.innerHTML = blueprint.innerHTML.replaceAll('__ROW__', `n${String(added)}`);

  const node = holder.firstElementChild;
  if (!(node instanceof HTMLElement)) return null;

  list.append(node);

  // The row template's menus are still native: they lived inside a
  // `<template>`, which nothing walked at startup.
  upgradeSelects(document);

  form.dispatchEvent(new Event('change', { bubbles: true }));
  return node;
}

function rebuildOptions(form: HTMLFormElement, options: PollOption[]): void {
  const list = $<HTMLElement>('[data-list="option"]', form);
  if (!list) return;

  list.replaceChildren();

  for (const option of options) {
    const row = addOption(form);
    if (!row) continue;

    setField(row, 'label', option.label);
    setField(row, 'emoji', option.emoji);
  }
}

/* ------------------------------------------------------------------- champs */

/** An ISO date, reduced to what a `datetime-local` can display. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (value: number) => String(value).padStart(2, '0');
  return (
    `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function text(form: HTMLFormElement, name: string): string {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function setValue(form: HTMLFormElement, name: string, next: string): void {
  const node = form.elements.namedItem(name);
  if (node instanceof HTMLInputElement) node.value = next;
}

function fieldValue(root: ParentNode, field: string): string {
  const node = root.querySelector(`[data-field="${CSS.escape(field)}"]`);
  return node instanceof HTMLInputElement ? node.value.trim() : '';
}

function setField(root: ParentNode, field: string, next: string): void {
  const node = root.querySelector(`[data-field="${CSS.escape(field)}"]`);
  if (node instanceof HTMLInputElement) node.value = next;
}

function number(form: HTMLFormElement, name: string): number {
  const node = form.elements.namedItem(name);
  const value = node instanceof HTMLInputElement ? Number(node.value) : 0;
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function checked(form: HTMLFormElement, name: string): boolean {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement ? node.checked : false;
}
